import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../api';
import { avg, record } from '../format';
import type { Appearance, DiscoverHistoryResult, HistoryScan, RatedPlayer } from '../types';

const germanDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–';

/**
 * Player history across leagues and regions.
 *
 * Upstream has no player-centric endpoint, so history has to be discovered by
 * scanning the organizer's leagues. That is a long, deliberate operation, hence
 * an explicit button rather than something that happens silently on import.
 */
export function HistoryPanel({ leagueId }: { leagueId: number }) {
  const [appearances, setAppearances] = useState<Appearance[]>([]);
  const [scan, setScan] = useState<HistoryScan | null>(null);
  const [players, setPlayers] = useState<RatedPlayer[]>([]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<DiscoverHistoryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeSameSeason, setIncludeSameSeason] = useState(false);
  const [onlyWithoutData, setOnlyWithoutData] = useState(false);

  const load = useCallback(async () => {
    try {
      const [history, p] = await Promise.all([api.leagueHistory(leagueId), api.players(leagueId)]);
      setAppearances(history.appearances);
      setScan(history.lastScan);
      setPlayers(p);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [leagueId]);

  useEffect(() => {
    void load();
  }, [load]);

  const runDiscovery = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const discovered = await api.discoverHistory(leagueId, { includeSameSeason });
      setResult(discovered);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setRunning(false);
    }
  };

  const playerById = useMemo(() => new Map(players.map((p) => [p.playerId, p])), [players]);

  /** Group appearances by player, newest first, so a career reads top to bottom. */
  const grouped = useMemo(() => {
    const byPlayer = new Map<number, Appearance[]>();
    for (const a of appearances) {
      const list = byPlayer.get(a.playerId) ?? [];
      list.push(a);
      byPlayer.set(a.playerId, list);
    }
    return [...byPlayer.entries()]
      .filter(([playerId]) => !onlyWithoutData || playerById.get(playerId)?.statsSource !== 'season')
      .sort((a, b) => {
        const nameA = a[1][0]?.displayName ?? '';
        const nameB = b[1][0]?.displayName ?? '';
        return nameA.localeCompare(nameB, 'de');
      });
  }, [appearances, onlyWithoutData, playerById]);

  const withoutHistory = players.filter((p) => !appearances.some((a) => a.playerId === p.playerId));

  return (
    <>
      <section className="card">
        <header>
          <h2>Player history across leagues &amp; regions</h2>
          <p className="hint">
            {scan
              ? `Last search: ${scan.leaguesScanned} leagues read, ${scan.leaguesWithHits} contained players of this league, ${scan.appearancesRecorded} records stored.`
              : 'No search has been run for this league yet.'}
          </p>
        </header>

        {error && <div className="notice error">{error}</div>}

        <p className="hint" style={{ marginBottom: 12 }}>
          The source site offers no way to look a player up directly, so finding where someone
          played before means reading every league the organizer publishes — around 260 of them,
          across all regions and seasons. Records found here feed the previous-season carry-over
          used by the predictions.
        </p>

        <div className="row" style={{ marginBottom: 12 }}>
          <button className="btn primary" onClick={() => void runDiscovery()} disabled={running}>
            {running ? 'Searching… (this takes a few minutes)' : 'Search other leagues & regions'}
          </button>
          <label className="check" title="Also read leagues from this league's own season">
            <input
              type="checkbox"
              checked={includeSameSeason}
              onChange={(e) => setIncludeSameSeason(e.target.checked)}
              disabled={running}
            />
            include the current season
          </label>
        </div>

        {running && (
          <div className="notice">
            Reading the organizer’s leagues one at a time, deliberately slowly so the source server
            is not hammered. Leave this tab open.
          </div>
        )}

        {result && (
          <div className="notice ok">
            Read {result.leaguesScanned} leagues looking for {result.playersLookedFor} players.
            Found {result.appearancesRecorded} records for {result.playersWithHistory} of them across{' '}
            {result.leaguesWithHits} leagues
            {result.skippedForLimit > 0 && ` (${result.skippedForLimit} leagues skipped by the limit)`}.
          </div>
        )}

        {result && result.hits.length > 0 && (
          <div className="table-scroll" style={{ marginTop: 12 }}>
            <table>
              <thead>
                <tr>
                  <th className="left">League found in</th>
                  <th className="left">Region</th>
                  <th className="left">Season</th>
                  <th>Players</th>
                </tr>
              </thead>
              <tbody>
                {result.hits.map((hit) => (
                  <tr key={hit.eventId}>
                    <td className="left">
                      <a href={hit.url} target="_blank" rel="noreferrer">
                        {hit.league} ↗
                      </a>
                    </td>
                    <td className="left muted">{hit.region ?? '–'}</td>
                    <td className="left muted">{hit.season ?? '–'}</td>
                    <td className="num">{hit.players}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card">
        <header>
          <h2>Records found</h2>
          <label className="check">
            <input
              type="checkbox"
              checked={onlyWithoutData}
              onChange={(e) => setOnlyWithoutData(e.target.checked)}
            />
            only players without results this season
          </label>
        </header>

        {grouped.length === 0 ? (
          <div className="empty-state">
            Nothing found yet. Run the search above to look for these players in other leagues.
          </div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="left">Player</th>
                  <th className="left">Season</th>
                  <th className="left" title="The league's own date, or the season start when it has none">
                    Date
                  </th>
                  <th className="left">League</th>
                  <th className="left">Region</th>
                  <th className="left">Team</th>
                  <th>Place</th>
                  <th>Matches</th>
                  <th>Legs</th>
                  <th>Average</th>
                  <th>180</th>
                </tr>
              </thead>
              <tbody>
                {grouped.map(([playerId, rows]) =>
                  rows.map((a, index) => (
                    <tr key={`${playerId}-${a.eventId}`}>
                      <td className="left">
                        {index === 0 ? (
                          <strong>{a.displayName}</strong>
                        ) : (
                          <span className="muted" style={{ paddingLeft: 12 }}>
                            ↳
                          </span>
                        )}
                      </td>
                      <td className="left">{a.seasonName ?? '–'}</td>
                      {/* The event's own date is more specific than the season
                          start, which is identical for every league of a season. */}
                      <td className="left muted">{germanDate(a.playedAt ?? a.seasonStart)}</td>
                      <td className="left">
                        <a href={a.url} target="_blank" rel="noreferrer">
                          {a.leagueName} ↗
                        </a>
                      </td>
                      <td className="left muted">{a.regionName ?? '–'}</td>
                      <td className="left muted">{a.teamName ?? '–'}</td>
                      <td className="num muted">{a.place ?? '–'}</td>
                      <td className="num">{record(a.singleWon, a.singleLost)}</td>
                      <td className="num">{record(a.singleLegsWon, a.singleLegsLost)}</td>
                      <td className="num">
                        <strong>{avg(a.average)}</strong>
                      </td>
                      <td className="num muted">{a.count180}</td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        )}

        {withoutHistory.length > 0 && (
          <p className="hint" style={{ marginTop: 12 }}>
            {withoutHistory.length} of {players.length} players have no record anywhere the search
            has looked. Either they are new to the organizer, or their previous league is outside
            the seasons the source still publishes.
          </p>
        )}
      </section>
    </>
  );
}
